import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";

import {
  activateRuleSet,
  createRuleSet,
  deleteRuleSet,
  fetchRules,
  saveRuleSet,
  type RuleSetRow,
  type RuleValue,
  type RulesView,
  type ServerRules,
} from "../../services/http.js";
import { cn } from "../../lib/utils.js";

/**
 * The game's numbers — phase 22, **ADR 0031**.
 *
 * # Why it exists
 *
 * The open balance questions — the void cap, the wildcard ratio, the undated
 * bonus, the Deathmatch pot, both of ADR 0030's stall clocks — are answered by a
 * real party and nothing else. Until this screen, changing one cost an image
 * build, a push and **a server restart in the middle of the party being
 * measured**: the feedback loop was longer than the party.
 *
 * # 🔴 The editor is driven by the keys the server sent
 *
 * Not by a hand-written list of forty-eight fields. `Rules` is serialized as
 * itself on the Rust side rather than through a config-shaped mirror, because
 * *"a mapping layer between two structs with the same fields exists only to
 * drift"* — and hand-writing that mirror here, where the plain-HTTP half has no
 * generator to check it, would reintroduce exactly that drift one language
 * further out.
 *
 * So a knob added to `Rules` appears here with no frontend change, and one
 * removed stops appearing. **The labels are humanised from the keys** for the
 * same reason: a translated label per knob is a second list to keep in step, and
 * this is a screen only an admin sees. The chrome around it is translated
 * normally.
 *
 * # ⚠️ Activating takes effect from the next game
 *
 * A party in flight keeps the rules it started with — `rules_for` hands the
 * engine an owned `Rules` that it holds for the life of the game. That is the
 * desired behaviour: changing a price between two rounds would rewrite the
 * economics of a round already being played.
 */
export function RulesPanel() {
  const { t } = useTranslation();
  const [view, setView] = useState<RulesView | null>(null);
  const [missing, setMissing] = useState(false);
  const [editing, setEditing] = useState<string | null>(null);
  const [draft, setDraft] = useState<ServerRules | null>(null);
  const [name, setName] = useState("");
  const [busy, setBusy] = useState(false);
  const [problem, setProblem] = useState<string | null>(null);

  const reload = () => {
    void fetchRules().then(setView, () => {
      setMissing(true);
    });
  };
  useEffect(reload, []);

  if (missing) {
    return <p className="text-sm text-white/50">{t("admin.nothingHere")}</p>;
  }
  if (view === null) {
    return <p className="text-sm text-white/50">{t("rules.reading")}</p>;
  }

  const act = (what: () => Promise<unknown>) => {
    setBusy(true);
    setProblem(null);
    void what().then(
      () => {
        setBusy(false);
        setEditing(null);
        setDraft(null);
        reload();
      },
      (error: unknown) => {
        setBusy(false);
        // The server's own sentence. `Rules::validate` explains itself — "a
        // guess window of zero closes before anybody can answer" — and replacing
        // that with "invalid" would throw away the only thing that says what to
        // change.
        setProblem(error instanceof Error ? error.message : t("rules.failed"));
      },
    );
  };

  const open = (set: RuleSetRow) => {
    setEditing(set.id);
    setDraft(set.rules);
    setName(set.name);
    setProblem(null);
  };

  return (
    <section className="flex flex-col gap-6">
      <header>
        <h2 className="text-lg font-semibold">{t("rules.title")}</h2>
        <p className="mt-1 max-w-2xl text-sm text-white/60">{t("rules.lead")}</p>
        {/* The file is not being read. Said here rather than only in a startup
            log an operator will never see. */}
        <p className="mt-2 max-w-2xl text-sm text-amber-300/80">
          {t("rules.fileIgnored")}
        </p>
      </header>

      {problem !== null && (
        <p
          role="alert"
          className="rounded-lg border border-wrong-500/50 bg-wrong-500/10 p-3 text-sm"
        >
          {problem}
        </p>
      )}

      <ul className="flex flex-col gap-3">
        {view.sets.map((set) => (
          <li
            key={set.id}
            className={cn(
              "rounded-xl border p-4",
              set.is_active ? "border-flash-500/60 bg-flash-500/10" : "border-white/15",
            )}
          >
            <div className="flex flex-wrap items-center gap-3">
              <span className="text-base font-semibold">{set.name}</span>
              {set.is_active && (
                <span className="rounded-md bg-flash-500/25 px-2 py-1 text-xs font-semibold uppercase tracking-wider">
                  {t("rules.active")}
                </span>
              )}
              <span className="ml-auto flex gap-2">
                <Action
                  label={t("rules.edit")}
                  onClick={() => {
                    open(set);
                  }}
                  disabled={busy}
                />
                {!set.is_active && (
                  <>
                    <Action
                      label={t("rules.activate")}
                      onClick={() => {
                        act(() => activateRuleSet(set.id));
                      }}
                      disabled={busy}
                    />
                    <Action
                      label={t("rules.delete")}
                      onClick={() => {
                        act(() => deleteRuleSet(set.id));
                      }}
                      disabled={busy}
                    />
                  </>
                )}
              </span>
            </div>

            {editing === set.id && draft !== null && (
              <Editor
                rules={draft}
                name={name}
                busy={busy}
                onName={setName}
                onChange={setDraft}
                onReset={() => {
                  setDraft(view.defaults);
                }}
                onSave={() => {
                  act(() => saveRuleSet(set.id, name.trim(), draft));
                }}
                onCopy={() => {
                  act(() => createRuleSet(name.trim(), draft));
                }}
                onCancel={() => {
                  setEditing(null);
                  setDraft(null);
                }}
              />
            )}
          </li>
        ))}
      </ul>
    </section>
  );
}

/** One rule set's numbers, as fields. */
function Editor({
  rules,
  name,
  busy,
  onName,
  onChange,
  onReset,
  onSave,
  onCopy,
  onCancel,
}: {
  rules: ServerRules;
  name: string;
  busy: boolean;
  onName: (next: string) => void;
  onChange: (next: ServerRules) => void;
  onReset: () => void;
  onSave: () => void;
  onCopy: () => void;
  onCancel: () => void;
}) {
  const { t } = useTranslation();

  const setFlat = (key: string, value: RuleValue) => {
    onChange({ ...rules, [key]: value });
  };
  const setNested = (group: string, key: string, value: RuleValue) => {
    const current = rules[group];
    if (current === undefined || typeof current !== "object") {
      return;
    }
    onChange({ ...rules, [group]: { ...current, [key]: value } });
  };

  // Flat keys first, then each nested group. Object.entries preserves the
  // server's own order, which is the struct's declaration order — so the screen
  // reads the way `rules/mod.rs` reads.
  const flat = Object.entries(rules).filter(
    (entry): entry is [string, RuleValue] => typeof entry[1] !== "object",
  );
  const groups = Object.entries(rules).filter(
    (entry): entry is [string, Record<string, RuleValue>] =>
      typeof entry[1] === "object" && entry[1] !== null,
  );

  return (
    <div className="mt-4 flex flex-col gap-5 border-t border-white/10 pt-4">
      <label className="flex flex-col gap-1 text-sm">
        <span className="text-white/60">{t("rules.name")}</span>
        <input
          value={name}
          onChange={(e) => {
            onName(e.target.value);
          }}
          className="min-h-11 rounded-lg border border-white/20 bg-stage-900 px-3"
        />
      </label>

      <Fields entries={flat} onChange={setFlat} />
      {groups.map(([group, values]) => (
        <fieldset key={group} className="flex flex-col gap-3">
          <legend className="text-sm font-semibold text-white/70">
            {humanise(group)}
          </legend>
          <Fields
            entries={Object.entries(values)}
            onChange={(key, value) => {
              setNested(group, key, value);
            }}
          />
        </fieldset>
      ))}

      <div className="flex flex-wrap gap-2">
        <Action label={t("rules.save")} onClick={onSave} disabled={busy} />
        {/* Saving a copy is how a preset is made: edit the active one, rename
            it, keep both. Never active on creation, so tonight's party is not
            changed by somebody drafting a comparison. */}
        <Action label={t("rules.saveAsNew")} onClick={onCopy} disabled={busy} />
        <Action label={t("rules.reset")} onClick={onReset} disabled={busy} />
        <Action label={t("rules.cancel")} onClick={onCancel} disabled={busy} />
      </div>
    </div>
  );
}

/** A grid of one-line inputs. */
function Fields({
  entries,
  onChange,
}: {
  entries: [string, RuleValue][];
  onChange: (key: string, value: RuleValue) => void;
}) {
  return (
    <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
      {entries.map(([key, value]) => (
        <label key={key} className="flex flex-col gap-1 text-sm">
          <span className="text-white/60">{humanise(key)}</span>
          {typeof value === "number" ? (
            <input
              type="number"
              value={value}
              // ⚠️ An empty box parses as `NaN`, which serializes as `null` and
              // is rejected by the server with a message about the wrong thing.
              // Held at 0 instead, which `Rules::validate` refuses by name for
              // every key where zero is not allowed.
              onChange={(e) => {
                onChange(key, Number.parseInt(e.target.value, 10) || 0);
              }}
              className="min-h-11 rounded-lg border border-white/20 bg-stage-900 px-3 tabular-nums"
            />
          ) : typeof value === "boolean" ? (
            <input
              type="checkbox"
              checked={value}
              onChange={(e) => {
                onChange(key, e.target.checked);
              }}
              className="size-6 self-start"
            />
          ) : (
            <input
              value={value}
              onChange={(e) => {
                onChange(key, e.target.value);
              }}
              className="min-h-11 rounded-lg border border-white/20 bg-stage-900 px-3"
            />
          )}
        </label>
      ))}
    </div>
  );
}

function Action({
  label,
  onClick,
  disabled,
}: {
  label: string;
  onClick: () => void;
  disabled: boolean;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      className="min-h-11 rounded-lg border border-white/25 px-4 text-sm font-medium disabled:opacity-40"
    >
      {label}
    </button>
  );
}

/**
 * `stall_grace_seconds` → `Stall grace seconds`.
 *
 * Derived rather than looked up, which is what keeps this screen drift-proof: a
 * knob added to `Rules` gets a readable label without anybody adding one. See
 * the panel's own note on why the labels are not translated.
 */
function humanise(key: string): string {
  const words = key.replaceAll("_", " ");
  return words.charAt(0).toUpperCase() + words.slice(1);
}
